import { CSSProperties, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../../../buttons/Button'

import { BlockModel, CourtModel } from '../../../../../types/court/court'
import {
  displayDate,
  displayDay,
  displayShortDayOfWeek,
} from '../../../../../utils/dateUtils'

type BlockCalendarProps = {
  courts: CourtModel[]
  blocks: BlockModel[]
  days: Date[]
  onCellClick: (courtId: number, date: string) => void
}

type BlockCalendarCssVars = CSSProperties & {
  '--block-calendar-day-count': number
}

export const BlockCalendar = ({
  courts,
  blocks,
  days,
  onCellClick,
}: BlockCalendarProps) => {
  const { t, i18n } = useTranslation(['blocks', 'courts'])

  const calendarDays = useMemo(
    () =>
      days.map(day => {
        const date = displayDate(day)

        return {
          date,
          weekday: displayShortDayOfWeek(day, i18n.language),
          dayNumber: displayDay(date),
        }
      }),
    [days, i18n.language]
  )

  const blockedDatesByCourtId = useMemo(() => {
    const map = new Map<number, Set<string>>()
    const dayDates = calendarDays.map(day => day.date)

    for (const block of blocks) {
      const blockStartDate = displayDate(block.startTime)
      const blockEndDate = displayDate(block.endTime)
      let courtDates = map.get(block.courtId)

      if (!courtDates) {
        courtDates = new Set()
        map.set(block.courtId, courtDates)
      }

      for (const date of dayDates) {
        if (date >= blockStartDate && date <= blockEndDate) {
          courtDates.add(date)
        }
      }
    }

    return map
  }, [blocks, calendarDays])

  const calendarRows = useMemo(
    () =>
      courts
        .filter((c): c is CourtModel & { id: number } => c.id != null)
        .map(court => {
          const { id, name, courtType } = court

          return {
            id,
            name,
            typeLabel: courtType
              ? t(`courts.type.${courtType}`, { ns: 'courts' })
              : undefined,
            cells: calendarDays.map(day => {
              const blocked =
                blockedDatesByCourtId.get(id)?.has(day.date) ?? false

              return {
                ...day,
                blocked,
                statusLabel: blocked
                  ? t('blocks.calendar.blocked')
                  : t('blocks.calendar.available'),
              }
            }),
          }
        }),
    [blockedDatesByCourtId, calendarDays, courts, t]
  )

  const calendarStyle = useMemo<BlockCalendarCssVars>(
    () => ({
      '--block-calendar-day-count': days.length,
    }),
    [days.length]
  )

  return (
    <section className="ph-card block-calendar" style={calendarStyle}>
      <div className="block-calendar-scroll">
        <div
          className="block-calendar-grid"
          role="grid"
          aria-label={t('blocks.calendar.aria-label')}
        >
          <div className="block-calendar-header" role="row">
            <div className="block-calendar-header-court" role="columnheader">
              {t('blocks.calendar.court')}
            </div>

            {calendarDays.map(day => (
              <div
                key={day.date}
                className="block-calendar-day-header"
                role="columnheader"
              >
                <div className="block-calendar-weekday">{day.weekday}</div>

                <div className="block-calendar-day-number">{day.dayNumber}</div>
              </div>
            ))}
          </div>

          {calendarRows.map(court => (
            <div key={court.id} className="block-calendar-row" role="row">
              <div className="block-calendar-court" role="rowheader">
                <div className="block-calendar-court-name">{court.name}</div>

                {court.typeLabel && (
                  <div className="mono tiny muted">{court.typeLabel}</div>
                )}
              </div>

              {court.cells.map(day => (
                <Button
                  key={`${court.id}-${day.date}`}
                  role="gridcell"
                  label={day.blocked ? day.statusLabel : undefined}
                  variant="ghost"
                  className={
                    day.blocked
                      ? 'block-calendar-cell is-blocked'
                      : 'block-calendar-cell'
                  }
                  title={day.statusLabel}
                  aria-label={`${court.name}, ${day.date}, ${day.statusLabel}`}
                  onClick={() => onCellClick(court.id, day.date)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
